import { ReactNode } from 'react';
import { notification } from 'antd';
import { useStore } from 'effector-react';
import { debounce } from 'lodash-es';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import {
  $isAuthorized,
  $userPermissions,
  logout,
} from '@/modules/auth/store/auth.store';
import { AuthService } from '@/modules/auth/services/auth.service';
import { DiContainer } from '@/modules/di/di-container';

import { DefaultPageTemplate } from '@/modules/common/components/default-page-template';
import logoSvg from '@@/home/images/logo.svg';
import tractorSvg from '@@/home/images/tractor-cropped.png';
import coverSvg from '@@/home/images/activity.svg';
import { ButtonMain } from '@/modules/common/components/button';
import {
  AgroCultIcon,
  AnaliticalMixing,
  GrainMixCalc,
  Home,
  Logistics,
  SellerAccountStatic,
  SimpleMix,
  Trades,
} from '@/shared/ui/icons';
import styles from './index.module.sass';
import { HomeLogoClippedIconImage } from '../../images';

export const HomeNewPage = () => {
  const sections: {
    img: any;
    title: ReactNode;
    description?: ReactNode;
    route: string;
    onClick?: () => void;
    disabled?: boolean;
  }[] = [
    {
      img: Home,
      title: (
        <>
          управление <br />
          зерновыми складами
        </>
      ),
      description:
        'Сервис помогает контролировать движение зерна, его приёмку, отгрузку и перемещение внутри склада. Возможность вести учёт в физическом и зачётном весе, проводить операции сушки и наблюдения за хранящимся зерном.',
      route: '/storage',
    },
    {
      img: AnaliticalMixing,
      title: (
        <>
          аналитика складов
          <br />и зерновых культур
        </>
      ),
      description:
        'Аналитика позволяет получить полную картину состояния зерновых запасов, повышает эффективность использования ресурсов и оперативного реагирования на изменения спроса и предложения.',
      route: '/mixing',
    },
    {
      img: SimpleMix,
      title: (
        <>
          интеллектуальное <br />
          миксование пшеницы
        </>
      ),
      description:
        'Инструмент, который помогает оптимизировать процесс смешивания пшеницы для достижения наилучших результатов и получения максимальной выгоды. С его помощью можно легко создавать смеси с заданными характеристиками.',
      route: '/mixing/recipes-new',
    },
    {
      img: Trades,
      title: (
        <>
          управление <br /> зерновыми сделками
        </>
      ),
      description:
        'Сервис позволяет быстро находить потенциальных партнёров, просматривать актуальные предложения, заключать выгодные контракты и совершать максимально выгодные сделки.',
      route: '/offer',
    },
    {
      img: GrainMixCalc,
      title: (
        <>
          продвинутый калькулятор <br />
          миксования пшеницы
        </>
      ),
      route: '/calculator',
      //disabled: PUBLIC_MODE === 'production',
    },
    {
      img: (args: any) => SearchIcon,
      title: (
        <>
          поиск зерна <br />
          по заданным параметрам
        </>
      ),
      route: '/search',
      //disabled: PUBLIC_MODE === 'production',
      // onClick: () => {
      //   navigate('mixing');
      //   setParamsModalOpen({ payload: true });
      // },
    },
    {
      img: AgroCultIcon,
      title: <>оформленные сделки</>,
      route: '/offer/contracts',
    },
    {
      img: SellerAccountStatic,
      title: (
        <>
          предложения <br />
          покупателей
        </>
      ),
      route: '/account/seller',
    },
    {
      img: Logistics,
      title: (
        <>
          логистика <br />
          продажи зерна
        </>
      ),
      route: '/mixing/logistics',
    },
    {
      img: (args: any) => OrderIcon,
      title: (
        <>
          стоимость складов по лучшей <br />
          рыночной цене без миксования
        </>
      ),
      route: '',
      onClick: () => {
        navigate('/best-prices');
        //setActivesModalOpen({ payload: true });
      },
    },
  ];
  const navigate = useNavigate();
  const authService = DiContainer.get(AuthService);

  const isAuthorized = useStore($isAuthorized);
  const permissions = useStore($userPermissions);

  const onLogin = () => authService.loginInSso();
  const onLoginBySectionLink = (link: string) =>
    authService.loginInSso({
      redirectUrl: window.location.origin + link,
    });
  const onSignUp = () => authService.signUpInSso();
  const onSignOut = () => {
    logout();
    authService.logout();
  };

  const showWarning = () => {
    notification.warning({
      message: 'Для начала работы в системе нужно авторизоваться',
    });
  };

  const warningAuth = debounce(showWarning, 500);

  return (
    <DefaultPageTemplate className={styles.root}>
      <div className={styles.topArea}>
        <div className={classNames(styles.side, styles.logoArea)}>
          <Link className={styles.logo} to="/">
            <img src={logoSvg} className={styles.logoIcon} />
            <span className={styles.main}>SBT</span>
            <span className={styles.additional}>
              cервис учёта хранения, <br />
              миксования и продажи зерна
            </span>
          </Link>
        </div>
        <div className={styles.side}>
          {!isAuthorized ? (
            <>
              <ButtonMain className={styles.logIn} label="войти" onClick={onLogin} />
              <ButtonMain
                className={styles.signUp}
                label="регистрация"
                onClick={() => {
                  navigate('/signup');
                }}
              />
            </>
          ) : (
            <ButtonMain className={styles.logIn} label="выйти" onClick={onSignOut} />
          )}
        </div>
      </div>
      <div className={styles.mainArea}>
        <div className={styles.side}>
          {sections.map((item, i) => (
            <Link
              onClick={
                item.disabled
                  ? (e) => e.preventDefault()
                  : isAuthorized
                  ? item.onClick
                    ? (e) => {
                        e.preventDefault();
                        item.onClick && item.onClick();
                      }
                    : undefined
                  : (e) => {
                      e.preventDefault();
                      warningAuth();
                    }
              }
              to={item.route}
              key={i}
              className={classNames(
                styles.sectionButton,
                item.description && styles.bigView,
                !isAuthorized && styles.unauthorized,
                item.disabled && styles.disabledLink,
              )}
            >
              <div className={styles.topContent}>
                <div className={styles.icon}>
                  {item.img({ fill: '#66B28A', isActive: true })}
                </div>
                <span className={styles.title}>{item.title}</span>
              </div>
              {item.description && (
                <p className={styles.description}>{item.description}</p>
              )}
            </Link>
          ))}
        </div>
        <div className={classNames(styles.side, styles.coverImg)}>
          <img className={styles.sideImg} src={tractorSvg} />
        </div>
      </div>
      <div className={styles.bottomArea}>
        <ButtonMain
          className={styles.begin}
          onClick={() => {
            isAuthorized
              ? navigate('/storage')
              : onLoginBySectionLink(window.location.href);
          }}
          label="приступить к работе в сервисе"
        />
        {/* { PUBLIC_MODE === 'development' && <ButtonMain
          className={styles.begin}
          onClick={() => {
            navigate('/buyer-account');
          }}
          label="кабинет покупателя (dev only)"
        /> } */}
        <p className={styles.info}>
          <b>Storage, Blend & Trade</b> - это сервис учета хранения и миксования
          агрокультур, который <br />
          помогает увеличивать прибыль и снижать расходы на хранение и логистику{' '}
        </p>
      </div>
      <img className={styles.logoClippedIcon} src={HomeLogoClippedIconImage} alt="" />
      <img className={styles.coverBgImg} src={coverSvg} />
    </DefaultPageTemplate>
  );
};

var OrderIcon = (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="48" height="48" rx="8" fill="#E1F3EA" />
    <g clip-path="url(#clip0_6753_22070)">
      <rect x="9.5" y="9.5" width="29" height="29" rx="4" fill="#66B28A" />
      <rect x="17.2917" y="15" width="18.2083" height="4" rx="2" fill="#E1F3EA" />
      <rect x="12.5" y="15" width="2.875" height="4" rx="1.4375" fill="#E1F3EA" />
      <rect x="17.2917" y="22" width="18.2083" height="4" rx="2" fill="#E1F3EA" />
      <rect x="12.5" y="22" width="2.875" height="4" rx="1.4375" fill="#E1F3EA" />
      <rect x="17.2917" y="29" width="18.2083" height="4" rx="2" fill="#E1F3EA" />
      <rect x="12.5" y="29" width="2.875" height="4" rx="1.4375" fill="#E1F3EA" />
    </g>
    <defs>
      <clipPath id="clip0_6753_22070">
        <rect width="29" height="29" fill="white" transform="translate(9.5 9.5)" />
      </clipPath>
    </defs>
  </svg>
);

var SearchIcon = (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="48" height="48" rx="8" fill="#E1F3EA" />
    <g clip-path="url(#clip0_6199_1963)">
      <path
        d="M34.5592 30.0824C39.1471 25.4943 39.147 18.029 34.5592 13.4409C29.9711 8.8529 22.5059 8.8529 17.9179 13.4409C14.0256 17.3333 13.4361 23.1221 16.1479 27.6426C16.1479 27.6426 16.3427 27.9694 16.0796 28.2323L10.076 34.2359C8.88107 35.4307 8.5966 37.1016 9.65628 38.1615L9.83873 38.3437C10.8984 39.4036 12.5694 39.1192 13.7642 37.9243L19.7552 31.9333C20.0309 31.6576 20.3576 31.8524 20.3576 31.8524C24.878 34.5641 30.6668 33.9747 34.5592 30.0824ZM20.0904 27.9098C16.7003 24.5197 16.7004 19.0037 20.0905 15.6136C23.4806 12.2237 28.9966 12.2236 32.3866 15.6136C35.7766 19.0036 35.7766 24.5197 32.3866 27.9098C28.9965 31.2997 23.4806 31.2997 20.0904 27.9098Z"
        fill="#66B28A"
      />
      <path
        d="M20.8412 21.0634C20.6834 21.0634 20.523 21.0324 20.3684 20.9671C19.7508 20.7057 19.4618 19.9931 19.7232 19.3753C21.3661 15.4928 25.8613 13.6708 29.7437 15.3137C30.3614 15.5751 30.6503 16.2878 30.3889 16.9055C30.1274 17.5232 29.415 17.8119 28.7971 17.5506C26.1482 16.4298 23.081 17.6729 21.9602 20.3218C21.7641 20.7851 21.3144 21.0634 20.8412 21.0634Z"
        fill="#66B28A"
      />
    </g>
    <defs>
      <clipPath id="clip0_6199_1963">
        <rect width="29" height="29" fill="white" transform="translate(9 10)" />
      </clipPath>
    </defs>
  </svg>
);
